import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Send, Inbox, Settings, Building2, FileText, CheckCircle2, Clock, 
  AlertCircle, Loader2, RefreshCw, Eye, Upload, Scale, Shield
} from 'lucide-react';

const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

// Status-Farben
const STATUS_FARBEN = {
  eingereicht: 'bg-blue-100 text-blue-700',
  in_bearbeitung: 'bg-amber-100 text-amber-700',
  zugestellt: 'bg-emerald-100 text-emerald-700',
  gelesen: 'bg-green-100 text-green-700',
  bestaetigt: 'bg-green-100 text-green-700',
  fehler: 'bg-red-100 text-red-700',
};

const STATUS_LABELS = {
  eingereicht: 'Eingereicht',
  in_bearbeitung: 'In Bearbeitung',
  zugestellt: 'Zugestellt',
  gelesen: 'Gelesen',
  bestaetigt: 'Bestätigt',
  fehler: 'Fehler',
};

export default function EGVPPage() {
  // State
  const [config, setConfig] = useState(null);
  const [gerichte, setGerichte] = useState([]);
  const [nachrichten, setNachrichten] = useState([]);
  const [posteingang, setPosteingang] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('ausgang');
  
  // Senden Dialog
  const [sendenOpen, setSendenOpen] = useState(false);
  const [sendingForm, setSendingForm] = useState({
    empfaenger_safe_id: '',
    empfaenger_name: '',
    betreff: '',
    aktenzeichen: '',
    nachricht_text: ''
  });
  const [sending, setSending] = useState(false);
  const [files, setFiles] = useState([]);

  // Laden
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [configRes, gerichteRes, nachrichtenRes, posteingangRes] = await Promise.all([
        fetch(`${API}/api/egvp/konfiguration`),
        fetch(`${API}/api/egvp/gerichte`),
        fetch(`${API}/api/egvp/nachrichten`),
        fetch(`${API}/api/egvp/posteingang`)
      ]);
      
      if (configRes.ok) setConfig(await configRes.json());
      if (gerichteRes.ok) {
        const d = await gerichteRes.json();
        setGerichte(d.gerichte || []);
      }
      if (nachrichtenRes.ok) {
        const d = await nachrichtenRes.json();
        setNachrichten(d.nachrichten || []);
      }
      if (posteingangRes.ok) {
        const d = await posteingangRes.json();
        setPosteingang(d.nachrichten || []);
      }
    } catch (e) {
      console.error('Laden fehlgeschlagen:', e);
    }
    setLoading(false);
  };

  // Status aktualisieren
  const updateStatus = async (nachrichtId) => {
    try {
      const res = await fetch(`${API}/api/egvp/status/${nachrichtId}`);
      if (res.ok) {
        const status = await res.json();
        setNachrichten(prev => prev.map(n => 
          n.id === nachrichtId ? { ...n, status: status.status } : n
        ));
        toast.success(`Status: ${status.status}`);
      }
    } catch (e) {
      toast.error('Status-Abfrage fehlgeschlagen');
    }
  };

  // Zustellungsbestätigung
  const zustellungsbestaetigung = async (nachrichtId) => {
    try {
      const res = await fetch(`${API}/api/egvp/nachrichten/${nachrichtId}/zustellungsbestaetigung`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setNachrichten(prev => prev.map(n => 
          n.id === nachrichtId ? { ...n, status: 'zugestellt', zustellungsbestaetigung_id: data.zustellungsbestaetigung_id } : n
        ));
        toast.success(`Zustellungsbestätigung: ${data.zustellungsbestaetigung_id}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Fehler');
      }
    } catch (e) {
      toast.error('Zustellungsbestätigung fehlgeschlagen');
    }
  };

  // Empfangsbekenntnis abgeben
  const empfangsbekenntnis = async (nachrichtId) => {
    try {
      const res = await fetch(`${API}/api/egvp/posteingang/${nachrichtId}/empfangsbekenntnis`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setPosteingang(prev => prev.map(n =>
          n.id === nachrichtId ? { ...n, empfangsbekenntnis_abgegeben: true, gelesen: true } : n
        ));
        toast.success(`Empfangsbekenntnis abgegeben: ${data.empfangsbekenntnis_id}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Fehler');
      }
    } catch (e) {
      toast.error('Empfangsbekenntnis fehlgeschlagen');
    }
  };

  // Gericht auswählen
  const selectGericht = (gerichtName) => {
    const gericht = gerichte.find(g => g.name === gerichtName);
    if (gericht) {
      setSendingForm(prev => ({
        ...prev,
        empfaenger_name: gericht.name,
        empfaenger_safe_id: gericht.safe_id
      }));
    }
  };

  // Nachricht senden
  const handleSenden = async () => {
    if (!sendingForm.empfaenger_safe_id || !sendingForm.betreff) {
      toast.error('Bitte Empfänger und Betreff eingeben');
      return;
    }
    
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('empfaenger_safe_id', sendingForm.empfaenger_safe_id);
      formData.append('empfaenger_name', sendingForm.empfaenger_name);
      formData.append('betreff', sendingForm.betreff);
      if (sendingForm.aktenzeichen) formData.append('aktenzeichen', sendingForm.aktenzeichen);
      if (sendingForm.nachricht_text) formData.append('nachricht_text', sendingForm.nachricht_text);
      
      for (const file of files) {
        formData.append('files', file);
      }
      
      const res = await fetch(`${API}/api/egvp/senden`, {
        method: 'POST',
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Nachricht eingereicht: ${data.nachricht_id}`);
        setSendenOpen(false);
        setSendingForm({ empfaenger_safe_id: '', empfaenger_name: '', betreff: '', aktenzeichen: '', nachricht_text: '' });
        setFiles([]);
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.error || 'Senden fehlgeschlagen');
      }
    } catch (e) {
      toast.error('Fehler beim Senden');
    }
    setSending(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="egvp-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Scale className="w-6 h-6 text-amber-600" />
            EGVP / beA
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Elektronisches Gerichts- und Verwaltungspostfach
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Modus-Badge */}
          {config?.modus === 'mock' ? (
            <Badge className="bg-amber-100 text-amber-700 border-amber-200">
              <AlertCircle className="w-3 h-3 mr-1" />
              Test-Modus
            </Badge>
          ) : (
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
              <Shield className="w-3 h-3 mr-1" />
              Produktiv
            </Badge>
          )}
          
          <Dialog open={sendenOpen} onOpenChange={setSendenOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="btn-egvp-senden">
                <Send className="w-4 h-4" />
                Neue Nachricht
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>EGVP-Nachricht senden</DialogTitle>
                <DialogDescription>
                  {config?.modus === 'mock' 
                    ? 'Test-Modus: Nachricht wird simuliert'
                    : 'Nachricht wird an das Gericht gesendet'}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Empfänger */}
                <div>
                  <Label>Empfänger (Gericht)</Label>
                  <Select onValueChange={selectGericht}>
                    <SelectTrigger data-testid="egvp-empfaenger">
                      <SelectValue placeholder="Gericht auswählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {gerichte.map(g => (
                        <SelectItem key={g.safe_id} value={g.name}>
                          {g.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {sendingForm.empfaenger_safe_id && (
                    <p className="text-xs text-slate-500 mt-1">
                      SAFE-ID: {sendingForm.empfaenger_safe_id}
                    </p>
                  )}
                </div>
                
                {/* Aktenzeichen */}
                <div>
                  <Label>Aktenzeichen</Label>
                  <Input 
                    value={sendingForm.aktenzeichen}
                    onChange={e => setSendingForm(prev => ({ ...prev, aktenzeichen: e.target.value }))}
                    placeholder="z.B. XVII 123/24"
                  />
                </div>
                
                {/* Betreff */}
                <div>
                  <Label>Betreff *</Label>
                  <Input 
                    value={sendingForm.betreff}
                    onChange={e => setSendingForm(prev => ({ ...prev, betreff: e.target.value }))}
                    placeholder="Betreff der Nachricht"
                  />
                </div>
                
                {/* Nachricht */}
                <div>
                  <Label>Nachricht (optional)</Label>
                  <Textarea 
                    value={sendingForm.nachricht_text}
                    onChange={e => setSendingForm(prev => ({ ...prev, nachricht_text: e.target.value }))}
                    placeholder="Zusätzlicher Text..."
                    rows={3}
                  />
                </div>
                
                {/* Anhänge */}
                <div>
                  <Label>Anhänge</Label>
                  <div className="border-2 border-dashed rounded-lg p-4 text-center">
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.docx"
                      onChange={e => setFiles(Array.from(e.target.files || []))}
                      className="hidden"
                      id="egvp-files"
                    />
                    <label htmlFor="egvp-files" className="cursor-pointer">
                      <Upload className="w-6 h-6 mx-auto mb-2 text-slate-400" />
                      <p className="text-sm text-slate-500">Dateien auswählen (PDF/DOCX)</p>
                    </label>
                    {files.length > 0 && (
                      <div className="mt-2 text-sm text-emerald-600">
                        {files.length} Datei(en) ausgewählt
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setSendenOpen(false)}>
                  Abbrechen
                </Button>
                <Button onClick={handleSenden} disabled={sending}>
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Senden
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Info-Banner für Mock-Modus */}
      {config?.modus === 'mock' && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-800">Test-Modus aktiv</h4>
              <p className="text-sm text-amber-700 mt-1">
                Alle EGVP-Nachrichten werden simuliert. Für echte Einreichungen setzen Sie bitte:
              </p>
              <ul className="text-sm text-amber-700 mt-2 list-disc list-inside">
                <li>EGVP_MODUS=produktion</li>
                <li>EGVP_SAFE_ID (Ihre SAFE-ID)</li>
                <li>EGVP_ZERTIFIKAT_PFAD (Pfad zum beA-Zertifikat)</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="ausgang" className="gap-2">
            <Send className="w-4 h-4" />
            Gesendet ({nachrichten.length})
          </TabsTrigger>
          <TabsTrigger value="eingang" className="gap-2">
            <Inbox className="w-4 h-4" />
            Posteingang ({posteingang.length})
          </TabsTrigger>
          <TabsTrigger value="gerichte" className="gap-2">
            <Building2 className="w-4 h-4" />
            Gerichte ({gerichte.length})
          </TabsTrigger>
        </TabsList>

        {/* Gesendet Tab */}
        <TabsContent value="ausgang" className="mt-4">
          {nachrichten.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Send className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium text-slate-700">Keine Nachrichten</h3>
                <p className="text-sm text-slate-500 mt-1">
                  Sie haben noch keine EGVP-Nachrichten gesendet.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {nachrichten.map(n => (
                <Card key={n.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-medium text-slate-800">{n.betreff}</h3>
                          <p className="text-sm text-slate-500">
                            An: {n.empfaenger?.name || 'Unbekannt'} • {n.aktenzeichen || 'Kein Az.'}
                          </p>
                          <p className="text-xs text-slate-400">
                            {new Date(n.created_date).toLocaleString('de-DE')} • ID: {n.id}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Badge className={STATUS_FARBEN[n.status] || 'bg-slate-100'}>
                          {STATUS_LABELS[n.status] || n.status}
                        </Badge>
                        {n.zustellungsbestaetigung_id && (
                          <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-300">
                            ZB: {n.zustellungsbestaetigung_id}
                          </Badge>
                        )}
                        {n.modus === 'mock' && (
                          <Badge variant="outline" className="text-xs">Mock</Badge>
                        )}
                        {n.status === 'eingereicht' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-xs gap-1"
                            onClick={() => zustellungsbestaetigung(n.id)}
                            data-testid={`btn-zb-${n.id}`}
                          >
                            <CheckCircle2 className="w-3 h-3" />
                            ZB
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => updateStatus(n.id)}
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Posteingang Tab */}
        <TabsContent value="eingang" className="mt-4">
          {posteingang.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Inbox className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium text-slate-700">Posteingang leer</h3>
                <p className="text-sm text-slate-500 mt-1">
                  Keine neuen Nachrichten im EGVP-Posteingang.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {posteingang.map(n => (
                <Card key={n.id} className={`hover:shadow-md transition-shadow ${!n.gelesen ? 'border-blue-200 bg-blue-50/30' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${!n.gelesen ? 'bg-blue-100' : 'bg-slate-100'}`}>
                          <Inbox className={`w-5 h-5 ${!n.gelesen ? 'text-blue-600' : 'text-slate-500'}`} />
                        </div>
                        <div>
                          <h3 className={`font-medium ${!n.gelesen ? 'text-slate-900' : 'text-slate-700'}`}>
                            {n.betreff}
                          </h3>
                          <p className="text-sm text-slate-500">
                            Von: {n.absender}
                          </p>
                          <p className="text-xs text-slate-400">
                            {new Date(n.datum).toLocaleString('de-DE')}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {!n.gelesen && <Badge className="bg-blue-100 text-blue-700">Neu</Badge>}
                        {n.empfangsbekenntnis_abgegeben && (
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                            EB abgegeben
                          </Badge>
                        )}
                        {n.modus === 'mock' && (
                          <Badge variant="outline" className="text-xs">Mock</Badge>
                        )}
                        {!n.empfangsbekenntnis_abgegeben && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-xs gap-1 border-amber-300 text-amber-700 hover:bg-amber-50"
                            onClick={() => empfangsbekenntnis(n.id)}
                            data-testid={`btn-eb-${n.id}`}
                          >
                            <CheckCircle2 className="w-3 h-3" />
                            Empfangsbekenntnis
                          </Button>
                        )}
                        <Button size="sm" variant="ghost">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Gerichte Tab */}
        <TabsContent value="gerichte" className="mt-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {gerichte.map(g => (
              <Card key={g.safe_id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Building2 className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <h3 className="font-medium text-slate-800">{g.name}</h3>
                      <p className="text-xs text-slate-500 mt-1">{g.adresse}</p>
                      <p className="text-xs text-slate-400 mt-2 font-mono">
                        {g.safe_id}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
